import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from './AuthContext';
import { instituteService } from '../services/instituteApi';

const AccessContext = createContext();

const EMPTY_ACCESS = {
  user_id: null,
  role: null,
  institute_id: null,
  enabled_modules: [],
  permissions: [],
};

export const AccessProvider = ({ children }) => {
  const { user, loading: authLoading } = useAuth();
  const [access, setAccess] = useState(EMPTY_ACCESS);
  const [loading, setLoading] = useState(true);

  const isPlatformSuperadmin = useMemo(() => {
    const isSuperadmin = (user?.role === 'superadmin' || user?.role === 'super_admin' || user?.role === 'admin') &&
      (user?.institute_id === null || user?.institute_id === undefined || user?.institute_id === 1);
    
  
    return isSuperadmin;
  }, [user]);

  const isInstituteScopedUser = useMemo(() => {
    const isScoped = Boolean(user?.institute_id) && !isPlatformSuperadmin;
    

    
    return isScoped;
  }, [user, isPlatformSuperadmin]);

  const loadAccess = async () => {
    
    if (authLoading) return;

    if (!user) {
      setAccess(EMPTY_ACCESS);
      setLoading(false);
      return;
    }

    if (isPlatformSuperadmin || !user?.institute_id || user?.institute_id === 1) {
      
      setAccess({
        user_id: user?.user_id || user?.id || null,
        role: user?.role || null,
        institute_id: user?.institute_id ?? null,
        enabled_modules: ['*'],
        permissions: ['*'],
      });
      setLoading(false);
      return;
    }

    console.log('🏢 INSTITUTE-SCOPED USER - FETCHING PERMISSIONS');
    setLoading(true);
    try {
      const res = await instituteService.getMyInstituteAccess();
      const payload = res?.data || {};
      
      
      setAccess({
        user_id: payload.user_id ?? user?.user_id ?? user?.id ?? null,
        role: payload.role ?? user?.role ?? null,
        institute_id: payload.institute_id ?? user?.institute_id ?? null,
        enabled_modules: Array.isArray(payload.enabled_modules) ? payload.enabled_modules : [],
        permissions: Array.isArray(payload.permissions) ? payload.permissions : [],
      });
    } catch (error) {
      
      // Default deny for tenant-scoped users if access API fails.
      setAccess({
        user_id: user?.user_id || user?.id || null,
        role: user?.role || null,
        institute_id: user?.institute_id ?? null,
        enabled_modules: [],
        permissions: [],
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAccess();
  }, [user, authLoading, isPlatformSuperadmin]);

  const hasModule = (moduleKey) => {
    if (!moduleKey) return true;
    // Bypass permission checking for admin users
    if (user?.role === 'admin') {
     
      return true;
    }
    // Always grant access to attendance module for admin users
    if (moduleKey === 'attendance' && user?.role === 'admin') {
      return true;
    }
    if (access.enabled_modules.includes('*')) {
      return true;
    }
    const hasAccess = access.enabled_modules.includes(moduleKey);
    return hasAccess;
  };

  const hasPermission = (permissionKey) => {
    if (!permissionKey) return true;
    if (access.permissions.includes('*')) return true;
    if (access.permissions.includes(permissionKey)) return true;

    const parts = permissionKey.split('.');
    for (let i = parts.length; i > 0; i -= 1) {
      const wildcard = `${parts.slice(0, i).join('.')}.*`;
      if (access.permissions.includes(wildcard)) return true;
    }
    return false;
  };

  const value = {
    access,
    loading,
    reloadAccess: loadAccess,
    hasModule,
    hasPermission,
    isPlatformSuperadmin,
    isInstituteScopedUser,
  };

  return <AccessContext.Provider value={value}>{children}</AccessContext.Provider>;
};

export const useAccess = () => {
  const context = useContext(AccessContext);
  if (!context) {
    throw new Error('useAccess must be used within AccessProvider');
  }
  return context;
};

export default AccessContext;
